import React, { useEffect, useReducer, useState } from "react";

import { toast } from "react-toastify";
import { format, parseISO } from "date-fns";
import {
	Pencil,
	Trash2,
	Plus,
	Ban,
	AlertTriangle,
	List,
	CalendarDays,
	Send,
	Loader2,
} from "lucide-react";

import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
	Table,
	TableHeader,
	TableBody,
	TableRow,
	TableHead,
	TableCell,
} from "../../components/ui/table";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "../../components/ui/tooltip";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import Title from "../../components/Title";
import toastError from "../../errors/toastError";
import { cn } from "../../lib/utils";
import api from "../../services/api";
import ScheduledMessageModal from "../../components/ScheduledMessageModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import ScheduleCalendar from "../../components/ScheduleCalendar";
import PageTabs from "../../components/PageTabs";

const reducer = (state, action) => {
	if (action.type === "LOAD_SCHEDULED_MESSAGES") {
		return [...action.payload];
	}

	if (action.type === "UPDATE_SCHEDULED_MESSAGES") {
		const scheduledMessage = action.payload;
		const index = state.findIndex((s) => s.id === scheduledMessage.id);

		if (index !== -1) {
			state[index] = { ...state[index], ...scheduledMessage };
			return [...state];
		} else {
			return [scheduledMessage, ...state];
		}
	}

	if (action.type === "DELETE_SCHEDULED_MESSAGE") {
		const id = action.payload;
		const index = state.findIndex((s) => s.id === id);
		if (index !== -1) {
			state.splice(index, 1);
		}
		return [...state];
	}

	if (action.type === "RESET") {
		return [];
	}
};

const truncate = (text, length = 60) => {
	if (!text) return "";
	return text.length > length ? `${text.slice(0, length)}...` : text;
};

// O nome do status sozinho não diz o que vai (ou não vai) acontecer.
const AJUDA_STATUS = {
	pending: "Ainda vai ser enviada na hora marcada.",
	sent: "Já saiu para o contato. Não dá para desfazer.",
	cancelled: "Não vai ser enviada. Está aqui só como histórico.",
	failed: "Tentou enviar e não conseguiu. Nada chegou ao contato.",
};

const statusBadge = (status) => {
	if (status === "pending") {
		return <Badge variant="default">Vai ser enviada</Badge>;
	}
	if (status === "sent") {
		return (
			<Badge variant="secondary" className="bg-green-100 text-green-800">
				Já enviada
			</Badge>
		);
	}
	if (status === "cancelled") {
		return <Badge variant="outline">Não será enviada</Badge>;
	}
	if (status === "failed") {
		return <Badge variant="destructive">Não conseguiu enviar</Badge>;
	}
	return <Badge variant="outline">{status}</Badge>;
};

// A data e a hora em que a mensagem saiu de fato. Só existe depois do primeiro
// envio; nos agendamentos que se repetem é o último envio, não o próximo.
const quandoEnviada = (scheduledMessage) => {
	if (!scheduledMessage.sentAt) return null;
	const quando = parseISO(scheduledMessage.sentAt);
	if (Number.isNaN(quando.getTime())) return null;
	const rotulo =
		scheduledMessage.status === "sent" ? "Enviado" : "Último envio";
	return `${rotulo} em ${format(quando, "dd/MM/yyyy")} às ${format(
		quando,
		"HH:mm"
	)}`;
};

const ScheduledMessages = () => {
	const [scheduledMessages, dispatch] = useReducer(reducer, []);
	const [loading, setLoading] = useState(false);

	const [modalOpen, setModalOpen] = useState(false);
	const [selectedMessage, setSelectedMessage] = useState(null);
	const [confirmModalOpen, setConfirmModalOpen] = useState(false);
	const [aba, setAba] = useState("lista");
	const [dataInicial, setDataInicial] = useState(null);
	// Qual linha está com o "Enviar agora" em andamento (trava o botão e
	// mostra o giro) — só uma por vez, o envio é imediato.
	const [enviandoAgoraId, setEnviandoAgoraId] = useState(null);

	useEffect(() => {
		(async () => {
			setLoading(true);
			try {
				const { data } = await api.get("/scheduled-messages");
				dispatch({ type: "LOAD_SCHEDULED_MESSAGES", payload: data });
				setLoading(false);
			} catch (err) {
				toastError(err);
				setLoading(false);
			}
		})();
	}, []);

	const handleOpenModal = () => {
		setModalOpen(true);
		setSelectedMessage(null);
		setDataInicial(null);
	};

	const handleCloseModal = () => {
		setModalOpen(false);
		setSelectedMessage(null);
		setDataInicial(null);
	};

	// Clique num dia do calendário: já entra com a data escolhida às 9h, que é
	// hora de expediente — meia-noite marcaria envio de madrugada sem querer.
	const handleSelecionarDia = (dia) => {
		setSelectedMessage(null);
		setDataInicial(`${format(dia, "yyyy-MM-dd")}T09:00`);
		setModalOpen(true);
	};

	const handleEditMessage = (scheduledMessage) => {
		setSelectedMessage(scheduledMessage);
		setModalOpen(true);
	};

	// Só o que ainda não saiu pode ser mudado. O resto é histórico: abrir o
	// formulário daria a impressão de que dá para corrigir algo já enviado.
	const handleAbrirDoCalendario = (scheduledMessage) => {
		if (scheduledMessage.status !== "pending") {
			toast.info(
				"Este agendamento já saiu da fila. Veja o histórico dele na aba Lista."
			);
			return;
		}
		handleEditMessage(scheduledMessage);
	};

	const handleMessageSaved = (scheduledMessage) => {
		dispatch({ type: "UPDATE_SCHEDULED_MESSAGES", payload: scheduledMessage });
	};

	const handleCloseConfirmationModal = () => {
		setConfirmModalOpen(false);
		setSelectedMessage(null);
	};

	const handleDeleteMessage = async (id) => {
		try {
			await api.delete(`/scheduled-messages/${id}`);
			dispatch({ type: "DELETE_SCHEDULED_MESSAGE", payload: id });
			toast.success("Agendamento excluído com sucesso!");
		} catch (err) {
			toastError(err);
		}
		setSelectedMessage(null);
	};

	const handleCancelMessage = async (scheduledMessage) => {
		try {
			const { data } = await api.post(
				`/scheduled-messages/${scheduledMessage.id}/cancel`
			);
			dispatch({ type: "UPDATE_SCHEDULED_MESSAGES", payload: data });
			toast.success("Agendamento cancelado com sucesso!");
		} catch (err) {
			toastError(err);
		}
	};

	// Manda a mensagem na hora, sem esperar a data marcada. O servidor usa
	// exatamente o mesmo envio do job automático; o que volta já traz o status
	// novo (enviada) e a data/hora do envio.
	const handleEnviarAgora = async (scheduledMessage) => {
		setEnviandoAgoraId(scheduledMessage.id);
		try {
			const { data } = await api.post(
				`/scheduled-messages/${scheduledMessage.id}/enviar-agora`
			);
			dispatch({ type: "UPDATE_SCHEDULED_MESSAGES", payload: data });
			toast.success("Mensagem enviada agora!");
		} catch (err) {
			toastError(err);
		}
		setEnviandoAgoraId(null);
	};

	return (
		<MainContainer>
			<ConfirmationModal
				title={
					selectedMessage &&
					`Apagar a mensagem agendada para ${
						selectedMessage.contact?.name || "esse contato"
					}?`
				}
				open={confirmModalOpen}
				onClose={handleCloseConfirmationModal}
				onConfirm={() => handleDeleteMessage(selectedMessage.id)}
			>
				{selectedMessage?.status === "pending"
					? "Ela some da lista e não será enviada. Não dá para voltar atrás."
					: "Ela some da lista. Isso não desfaz nada que já foi enviado."}
			</ConfirmationModal>
			<ScheduledMessageModal
				open={modalOpen}
				onClose={handleCloseModal}
				scheduledMessageId={selectedMessage?.id}
				onSaved={handleMessageSaved}
				dataInicial={dataInicial}
			/>
			<MainHeader>
				<Title>Mensagens agendadas</Title>
				<MainHeaderButtonsWrapper>
					<Button onClick={handleOpenModal}>
						<Plus className="h-4 w-4" />
						Agendar mensagem
					</Button>
				</MainHeaderButtonsWrapper>
			</MainHeader>
			<PageTabs
				abas={[
					{ value: "lista", rotulo: "Lista", icone: List },
					{ value: "calendario", rotulo: "Calendário", icone: CalendarDays },
				]}
				atual={aba}
				onChange={setAba}
				className="px-6"
			/>
			<div className="flex-1 overflow-auto px-4 pb-4 sm:px-6 sm:pb-6 pt-4">
				{/* Tudo nesta lista sai sozinho para o WhatsApp de um cliente. */}
				<div className="mb-4 rounded-xl border bg-muted p-3 text-xs leading-relaxed">
					<p className="flex items-start gap-1.5 font-medium">
						<AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
						Cada linha aqui é uma mensagem que o sistema envia sozinho, sem
						ninguém apertar nada.
					</p>
					<p className="mt-1.5 text-muted-foreground">
						Ele confere a cada minuto se chegou a hora de alguma. Para impedir um
						envio, clique em{" "}
						<span className="font-medium text-foreground">
							Cancelar o envio
						</span>{" "}
						na linha antes da hora marcada — depois de enviada, não tem volta.
					</p>
				</div>
				{aba === "calendario" && (
					<ScheduleCalendar
						agendamentos={scheduledMessages}
						onSelecionarDia={handleSelecionarDia}
						onAbrir={handleAbrirDoCalendario}
					/>
				)}
				{/* Tabela: do tablet para cima, e só na aba de lista. */}
				<div
					className={cn(
						"hidden rounded-xl border bg-card",
						aba === "lista" && "sm:block"
					)}
				>
					<Table>
						<TableHeader className="sticky top-0 z-10 bg-card">
							<TableRow>
								<TableHead>Quem vai receber</TableHead>
								<TableHead>O que será enviado</TableHead>
								<TableHead>Quando sai</TableHead>
								<TableHead className="text-center">Situação</TableHead>
								<TableHead className="text-center">Ações</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{scheduledMessages.map((scheduledMessage) => (
								<TableRow key={scheduledMessage.id}>
									<TableCell className="font-medium">
										{scheduledMessage.contact?.name || "-"}
										{scheduledMessage.contact?.number && (
											<span className="block text-xs font-normal tabular-nums text-muted-foreground">
												{scheduledMessage.contact.number}
											</span>
										)}
									</TableCell>
									<TableCell className="max-w-xs truncate text-muted-foreground">
										{truncate(scheduledMessage.body)}
									</TableCell>
									<TableCell>
										{scheduledMessage.scheduledFor
											? format(
													parseISO(scheduledMessage.scheduledFor),
													"dd/MM/yyyy HH:mm"
											  )
											: "-"}
									</TableCell>
									<TableCell className="text-center">
										{statusBadge(scheduledMessage.status)}
										{AJUDA_STATUS[scheduledMessage.status] && (
											<span className="mt-1 block text-[11px] leading-snug text-muted-foreground">
												{AJUDA_STATUS[scheduledMessage.status]}
											</span>
										)}
										{quandoEnviada(scheduledMessage) && (
											<span className="mt-0.5 block text-[11px] font-medium leading-snug text-green-700 dark:text-green-400">
												{quandoEnviada(scheduledMessage)}
											</span>
										)}
									</TableCell>
									<TableCell>
										<TooltipProvider>
											<div className="flex items-center justify-center gap-1">
												{scheduledMessage.status === "pending" && (
													<Tooltip>
														<TooltipTrigger asChild>
															<Button
																variant="ghost"
																size="icon"
																className="h-8 w-8 text-green-700 hover:text-green-700"
																disabled={
																	enviandoAgoraId === scheduledMessage.id
																}
																onClick={() =>
																	handleEnviarAgora(scheduledMessage)
																}
															>
																{enviandoAgoraId ===
																scheduledMessage.id ? (
																	<Loader2 className="h-4 w-4 animate-spin" />
																) : (
																	<Send className="h-4 w-4" />
																)}
															</Button>
														</TooltipTrigger>
														<TooltipContent>
															Enviar agora — sem esperar a hora marcada
														</TooltipContent>
													</Tooltip>
												)}
												{scheduledMessage.status === "pending" && (
													<Tooltip>
														<TooltipTrigger asChild>
															<Button
																variant="ghost"
																size="icon"
																className="h-8 w-8"
																onClick={() =>
																	handleEditMessage(scheduledMessage)
																}
															>
																<Pencil className="h-4 w-4" />
															</Button>
														</TooltipTrigger>
														<TooltipContent>
															Mudar o texto ou a hora
														</TooltipContent>
													</Tooltip>
												)}
												{scheduledMessage.status === "pending" && (
													<Tooltip>
														<TooltipTrigger asChild>
															<Button
																variant="ghost"
																size="icon"
																className="h-8 w-8"
																onClick={() =>
																	handleCancelMessage(scheduledMessage)
																}
															>
																<Ban className="h-4 w-4" />
															</Button>
														</TooltipTrigger>
														<TooltipContent>
															Cancelar o envio — nada será enviado
														</TooltipContent>
													</Tooltip>
												)}
												<Tooltip>
													<TooltipTrigger asChild>
														<Button
															variant="ghost"
															size="icon"
															className="h-8 w-8 text-destructive hover:text-destructive"
															onClick={() => {
																setSelectedMessage(scheduledMessage);
																setConfirmModalOpen(true);
															}}
														>
															<Trash2 className="h-4 w-4" />
														</Button>
													</TooltipTrigger>
													<TooltipContent>
														Apagar da lista
													</TooltipContent>
												</Tooltip>
											</div>
										</TooltipProvider>
									</TableCell>
								</TableRow>
							))}
							{loading && <TableRowSkeleton columns={5} />}
							{!loading && scheduledMessages.length === 0 && (
								<TableRow>
									<TableCell
										colSpan={5}
										className="py-10 text-center text-sm text-muted-foreground"
									>
										Nada agendado. O sistema não vai mandar nada sozinho por
										aqui.
										<span className="mt-1 block text-xs">
											Use “Agendar mensagem” para marcar um envio para um
											contato numa data e hora.
										</span>
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</div>

				{/* Agendamentos no celular.
				    Quando sai vem em destaque: é a informação que se confere
				    aqui. Editar e cancelar só aparecem enquanto está pendente —
				    mensagem já enviada não volta atrás. */}
				{aba === "lista" && (
					<ul className="space-y-2 sm:hidden">
						{scheduledMessages.map((scheduledMessage) => {
							const pendente = scheduledMessage.status === "pending";

							return (
								<li
									key={scheduledMessage.id}
									className="rounded-xl border bg-card"
								>
									<div className="space-y-1.5 p-3">
										<div className="flex items-start justify-between gap-3">
											<div className="min-w-0 flex-1">
												<p className="truncate font-medium">
													{scheduledMessage.contact?.name || "—"}
												</p>
												{scheduledMessage.contact?.number && (
													<p className="truncate text-xs tabular-nums text-muted-foreground">
														{scheduledMessage.contact.number}
													</p>
												)}
											</div>
											{statusBadge(scheduledMessage.status)}
										</div>

										<p className="line-clamp-2 text-sm text-muted-foreground">
											{truncate(scheduledMessage.body)}
										</p>

										<p className="text-sm font-medium tabular-nums">
											{scheduledMessage.scheduledFor
												? format(
														parseISO(scheduledMessage.scheduledFor),
														"dd/MM/yyyy 'às' HH:mm"
												  )
												: "Sem data"}
										</p>

										{AJUDA_STATUS[scheduledMessage.status] && (
											<p className="text-xs leading-snug text-muted-foreground">
												{AJUDA_STATUS[scheduledMessage.status]}
											</p>
										)}

										{quandoEnviada(scheduledMessage) && (
											<p className="text-xs font-medium text-green-700 dark:text-green-400">
												{quandoEnviada(scheduledMessage)}
											</p>
										)}
									</div>

									<div className="flex items-center justify-end gap-1 border-t px-2 py-1.5">
										{pendente && (
											<>
												<Button
													variant="ghost"
													size="icon"
													className="h-9 w-9 text-green-700 hover:text-green-700"
													aria-label="Enviar agora"
													disabled={
														enviandoAgoraId === scheduledMessage.id
													}
													onClick={() =>
														handleEnviarAgora(scheduledMessage)
													}
												>
													{enviandoAgoraId === scheduledMessage.id ? (
														<Loader2 className="h-4 w-4 animate-spin" />
													) : (
														<Send className="h-4 w-4" />
													)}
												</Button>
												<Button
													variant="ghost"
													size="icon"
													className="h-9 w-9"
													aria-label="Mudar o texto ou a hora"
													onClick={() => handleEditMessage(scheduledMessage)}
												>
													<Pencil className="h-4 w-4" />
												</Button>
												<Button
													variant="ghost"
													size="icon"
													className="h-9 w-9"
													aria-label="Cancelar o envio"
													onClick={() => handleCancelMessage(scheduledMessage)}
												>
													<Ban className="h-4 w-4" />
												</Button>
											</>
										)}
										<Button
											variant="ghost"
											size="icon"
											className="h-9 w-9 text-destructive hover:text-destructive"
											aria-label="Apagar da lista"
											onClick={() => {
												setSelectedMessage(scheduledMessage);
												setConfirmModalOpen(true);
											}}
										>
											<Trash2 className="h-4 w-4" />
										</Button>
									</div>
								</li>
							);
						})}

						{!loading && scheduledMessages.length === 0 && (
							<li className="rounded-xl border bg-card px-4 py-10 text-center text-sm text-muted-foreground">
								Nenhuma mensagem agendada.
							</li>
						)}
						{loading && (
							<li className="rounded-xl border bg-card px-4 py-6 text-center text-sm text-muted-foreground">
								Carregando...
							</li>
						)}
					</ul>
				)}
			</div>
		</MainContainer>
	);
};

export default ScheduledMessages;
